import { calculateBoundingBox, createConcaveHullPolygon } from "@nova/calculate-geospatial"
import { DistanceUnit, GeometryType, HotspotStatus, isUsable, LineWidth } from "@nova/constants"
import { Coordinate } from "@nova/protobuf"
import { Geometry } from "plugins/shared/layers/types"
import { selectFilteredHotspotsByFlightId } from "features/entity-caching/hotspots/selectors"
import { selectColorFinderImagesAndUploadsByFlightId } from "features/entity-caching/images/colorfinder/selectors"
import { selectInfraredImagesAndUploadsByFlightId } from "features/entity-caching/images/hotspots/selectors"
import { selectOrthoImagesAndUploadsByFlightId } from "features/entity-caching/images/orthos/selectors"
import { selectLinesMap } from "features/entity-caching/lines/selectors"
import { selectMergedFlightById } from "features/entity-caching/mergedFlights/selectors"
import { WebMercatorViewport } from "integrations/deckgl"
import store from "integrations/redux/store"
import { generateBufferLinesAsync } from "@nova/calculate-buffers"

const VIEWPORT_SIZE = 5000

export const autoCreatePerimeterPolygon = async (
    {
        hotspotFlightIds,
        flightPathFlightIds,
        lineIds,
        mergedFlightIDs,
        abortSignal,
        onError,
    }: {
        hotspotFlightIds: Set<string>
        flightPathFlightIds: Set<string>
        lineIds: Set<string>
        mergedFlightIDs: Set<string>
        abortSignal?: AbortSignal
        onError: (error: unknown) => void
    },
    offset = 1
): Promise<Coordinate[] | undefined> => {
    const state = store.getState()
    const linesMap = selectLinesMap(state)

    const hotspotPoints = Array.from(hotspotFlightIds).reduce((points, id) => {
        const hotspots = selectFilteredHotspotsByFlightId(id)(state)
        hotspots?.forEach((hotspot) => {
            if (hotspot.status === HotspotStatus.Removed) return

            points.push({ longitude: hotspot.longitude, latitude: hotspot.latitude })

            if (hotspot.polygon.length)
                hotspot.polygon.forEach((coordinate) => points.push({ ...coordinate }))
        })
        return points
    }, [] as Coordinate[])

    const mergedPoints = Array.from(mergedFlightIDs).reduce((points, id) => {
        const mergedFlight = selectMergedFlightById(id)(state)
        const hotspots = mergedFlight.hotspotFlightIds.flatMap((fId) =>
            selectFilteredHotspotsByFlightId(fId)(state)
        )

        hotspots.forEach((hotspot) => {
            if (hotspot.status === HotspotStatus.Removed) return
            points.push({ longitude: hotspot.longitude, latitude: hotspot.latitude })

            if (hotspot.polygon.length)
                hotspot.polygon.forEach((coordinate) => points.push({ ...coordinate }))
        })
        return points
    }, [] as Coordinate[])

    const flightPathPoints = Array.from(flightPathFlightIds).reduce((points, id) => {
        const infraredImages = selectInfraredImagesAndUploadsByFlightId(id)(state)
        const orthoImages = selectOrthoImagesAndUploadsByFlightId(id)(state)
        const colorFinderImages = selectColorFinderImagesAndUploadsByFlightId(id)(state)
        const images = infraredImages.length
            ? infraredImages
            : orthoImages.length
              ? orthoImages
              : colorFinderImages
        images?.forEach((image) => {
            const usable = isUsable(image)
            if (usable && usable.longitude !== undefined && usable.latitude !== undefined)
                points.push({ longitude: usable.longitude, latitude: usable.latitude })
        })
        return points
    }, [] as Coordinate[])

    const linePoints = Array.from(lineIds).reduce((points, id) => {
        const line = linesMap[id]
        if (line?.coordinates) points.push(...line.coordinates)
        return points
    }, [] as Coordinate[])

    const allPoints = [...hotspotPoints, ...flightPathPoints, ...linePoints, ...mergedPoints]
    if (!allPoints.length) return []

    const rawViewport = new WebMercatorViewport({ width: VIEWPORT_SIZE, height: VIEWPORT_SIZE })
    const box = calculateBoundingBox(allPoints)
    const { minlng, minlat, maxlng, maxlat } = box.bbox
    const viewport = rawViewport.fitBounds([
        [minlng, minlat],
        [maxlng, maxlat],
    ])

    // Project coordinates to [x,y].
    const projectedCoordinates: [number, number][] = allPoints.map(
        ({ longitude, latitude }) => viewport.project([longitude, latitude]) as [number, number]
    )

    const concaveHullPolygon = createConcaveHullPolygon(projectedCoordinates)

    // Unproject [x,y] to coordinates.
    const unprojectedCoordinates: Coordinate[] = concaveHullPolygon.map((position) => {
        const [longitude, latitude] = viewport.unproject(position)
        return { longitude, latitude }
    })

    const bufferedGeometries: Geometry[] = (
        await generateBufferLinesAsync(
            [{ coordinates: unprojectedCoordinates }],
            {
                range: [{ distance: -offset, color: 0, width: LineWidth.normal }],
                unit: DistanceUnit.Meters,
            },
            onError,
            abortSignal
        )
    ).flatMap((calculatedBuffers) =>
        calculatedBuffers.map(
            (calculatedBuffer) =>
                ({
                    ...calculatedBuffer,
                    geometryType: GeometryType.Polygon,
                }) satisfies Geometry
        )
    )

    const geometry = bufferedGeometries.at(0)

    return geometry && geometry.coordinates.length >= 3
        ? [...geometry.coordinates, geometry.coordinates[0]]
        : undefined
}
